import React, { useEffect, useState } from "react";
import { Plus, FileText, Trash2, Edit } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ClinicalNote } from "../types";
import { getClinicalNotesByPatientId, deleteClinicalNote, updateClinicalNote } from "../services/clinicalNotes";
import Modal from "../components/Common/Modal";
import CustomSelect from "../components/Common/CustomSelect";

interface Props {
  patientId: string;
  onDataChanged: () => void;
}

interface DetailRowProps {
  label: string;
  value: any;
  isMultiline?: boolean;
}

// Reusable multiline formatter
const formatMultilineText = (text?: string) => {
  if (!text) return null;

  return text
    .split(/[\r\n]+/)     // handles \n and \r\n
    .filter(line => line.trim() !== "")
    .map((line, index) => (
      <div key={index} className="whitespace-pre-wrap">
        {line}
      </div>
    ));
};


const DetailRow: React.FC<DetailRowProps> = ({ label, value, isMultiline }) => {
  if (!value) return null;

  return (
    <div>
      <b>{label}:</b>
      {isMultiline ? (
        <pre className="whitespace-pre-wrap mt-1 text-gray-700">{value}</pre>
      ) : (
        <span className="ml-2">{value}</span>
      )}
    </div>
  );
};

const ClinicalNotes: React.FC<Props> = ({ 
  patientId,
  onDataChanged,
}) => {
  const navigate = useNavigate();

  const [notes, setNotes] = useState<ClinicalNote[]>([]);
  const [selectedNote, setSelectedNote] = useState<ClinicalNote | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editData, setEditData] = useState<any>({});

  // Load Notes
  useEffect(() => {
    const loadNotes = async () => {
      try {
        const data = await getClinicalNotesByPatientId(String(patientId));
        setNotes(data);
      } catch (error) {
        console.error("Failed to load notes:", error);
      } finally {
        setLoading(false);
      }
    };

    loadNotes();
  }, [patientId]);

  const handleDelete = async () => {
    if (!selectedNote) return;

    try {
      await deleteClinicalNote(selectedNote.id);
      setNotes((prev) => prev.filter((n) => n.id !== selectedNote.id));

      onDataChanged();
      setSelectedNote(null);
      setDeleteModalOpen(false);
    } catch (error) {
      console.error("Delete failed:", error);
    }
  };

  const openEditModal = () => {
    if (!selectedNote) return;
    setEditData(selectedNote);
    setEditModalOpen(true);
  };

  const handleEditChange = (field: string, value: string) => {
    setEditData((prev: any) => ({ ...prev, [field]: value }));
  };

  const handleUpdate = async (filteredData: any) => {
    try {
      const updated = await updateClinicalNote(editData.id, filteredData);
      setNotes((prev) => prev.map((n) => (n.id === updated.id ? updated : n)));
      setSelectedNote(updated);

      onDataChanged();
      setEditModalOpen(false);
    } catch (error) {
      console.error("Update failed:", error);
    }
  };

  const getTypeStyles = (type: string) => {
    if (type === "subjective") return "bg-primary-100 text-primary-800";
    if (type === "objective") return "bg-green-100 text-green-800";
    if (type === "assessment") return "bg-yellow-100 text-yellow-800";
    // if( type === "plan") return "bg-purple-100 text-purple-800";
    return "bg-purple-100 text-purple-800";
  };

  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left — Notes List */}
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold text-gray-900">
              Clinical Notes
            </h3>

            <button
              onClick={() => navigate(`/patients/${patientId}/notes/new`,{
                state: {
                  returnTab: "clinical-notes"
                }
              })}
              className="btn-primary flex items-center space-x-2"
            >
              <Plus className="h-4 w-4" />
              <span>Add Note</span>
            </button>
          </div>

          {loading ? (
            <p>Loading notes...</p>
          ) : notes.length === 0 ? (
            <p>No notes found.</p>
          ) : (
            <div className="space-y-3">
              {notes.map((note) => (
                <div
                  key={note.id}
                  onClick={() => setSelectedNote(note)}
                  className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                    selectedNote?.id === note.id
                      ? "border-primary-500 bg-primary-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-medium">{note.title}</h4>

                      <p className="text-sm mt-1">
                        {note.clinical_impression?.substring(0, 100)}...
                      </p>

                      <p className="text-xs text-gray-500 mt-2">
                        {new Date(note.createdAt).toLocaleDateString()}
                      </p>
                    </div>

                    <span
                      className={`px-2 py-1 text-xs rounded-full ${getTypeStyles(
                        note.type
                      )}`}
                    >
                      {note.type}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right — Note Details */}
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Note Details</h3>

            {selectedNote && (
              <div className="flex items-center space-x-3">
                <button title="Edit"
                  onClick={openEditModal}
                  className="text-primary-500 hover:text-primary-800"
                >
                  <Edit className="w-6 h-6" />
                </button>

                <button title="Delete"
                  onClick={() => setDeleteModalOpen(true)}
                  className="text-red-600 hover:text-red-800"
                >
                  <Trash2 className="w-6 h-6" />
                </button>
              </div>
            )}
          </div>

          {!selectedNote ? (
            <div className="card text-center py-12">
              <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p>Select a note to view details</p>
            </div>
          ) : (
            <div className="card p-4 overflow-y-auto max-h-[600px] space-y-4">
              <h4 className="text-xl font-semibold">{selectedNote.title}</h4>

              <p className="text-sm text-gray-500">
                {new Date(selectedNote.createdAt).toLocaleString()}
              </p>

              <div className="space-y-3 text-sm text-gray-700">
                <DetailRow label="Type" value={selectedNote.type} />
                <DetailRow
                  label="History Presenting Complaints"
                  value={formatMultilineText(selectedNote.history_presenting_complaints)}
                  isMultiline
                />

                <DetailRow
                  label="Past Medical History"
                  value={formatMultilineText(selectedNote.past_medical_history)}
                  isMultiline
                />
                <DetailRow label="Medication" value={formatMultilineText(selectedNote.medication)} />
                <DetailRow
                  label="Family History"
                  value={formatMultilineText(selectedNote.family_history)}
                />
                <DetailRow
                  label="Social History"
                  value={formatMultilineText(selectedNote.social_history)}
                />
                <DetailRow
                  label="Clinical Examination Findings"
                  value={formatMultilineText(selectedNote.clinical_examination_findings)}
                />
                <DetailRow
                  label="Clinical Impression"
                  value={formatMultilineText(selectedNote.clinical_impression)}
                />
                <DetailRow
                  label="Full Blood Count"
                  value={formatMultilineText(selectedNote.full_blood_count)}
                />
                <DetailRow label="U&Es" value={formatMultilineText(selectedNote.u_and_es)} />
                <DetailRow label="LFT" value={formatMultilineText(selectedNote.lft)} />
                <DetailRow label="LDH" value={formatMultilineText(selectedNote.ldh)} />
                <DetailRow label="Uric Acid" value={formatMultilineText(selectedNote.uric_acid)} />
                <DetailRow label="Viral Serology Results" value={formatMultilineText(selectedNote.viral_serology_results)}/>
                <DetailRow label="ECHO" value={formatMultilineText(selectedNote.echo)} />
                <DetailRow
                  label="Additional Comments"
                  value={formatMultilineText(selectedNote.additional_comments)}
                  isMultiline
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* DELETE MODAL */}
      <Modal
        title="Delete Note"
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
      >
        <p className="text-gray-700">Are you sure you want to delete this note? This action cannot be undone.</p>

        <div className="flex justify-end space-x-3 mt-6">
          <button
            onClick={() => setDeleteModalOpen(false)}
            className="btn-secondary"
          >
            Cancel
          </button>
          <button
            onClick={handleDelete}
            className="btn-danger"
          >
            Delete
          </button>
        </div>
      </Modal>

      <Modal
        title="Edit Clinical Note"
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        widthClassName="max-w-3xl"
      >
        <div className="flex flex-col max-h-[70vh]">
          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto space-y-4 p-2">
            {Object.keys(editData)
              .filter(
                (field) =>
                  ![
                    "id",
                    "patient_id",
                    "createdAt",
                    "updatedAt",
                    "doctor_id",
                    "patient"
                  ].includes(field)
              )
              .map((field) => {
                if (typeof editData[field] !== "string") return null;

                const label = field.replace(/_/g, " ");

                // SOAP dropdown for `type`
                if (field === "type") {
                  return (
                    <div key={field}>
                      <label className="block text-sm font-semibold text-gray-700 mb-1 capitalize">
                        {label}
                      </label>

                      <CustomSelect
                        value={editData[field] || ""}
                        onChange={(val) => handleEditChange(field, val)}
                        options={[
                          { value: "", label: "Select type" },
                          { value: "Subjective", label: "Subjective" },
                          { value: "Objective", label: "Objective" },
                          { value: "Assessment", label: "Assessment" },
                          { value: "Plan", label: "Plan" },
                        ]}
                        placeholder="Select type"
                      />
                    </div>
                  );
                }

                // Default textarea for all other fields
                return (
                  <div key={field}>
                    <label className="block text-sm font-semibold text-gray-700 mb-1 capitalize">
                      {label}
                    </label>

                    <textarea
                      className="input-field"
                      value={editData[field] || ""}
                      onChange={(e) =>
                        handleEditChange(field, e.target.value)
                      }
                      rows={3}
                    />
                  </div>
                );
              })}
          </div>

          {/* Footer */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 bg-white">
            <button
              onClick={() => setEditModalOpen(false)}
              className="btn-secondary"
            >
              Cancel
            </button>

            <button
              onClick={async () => {
                const excluded = [
                  "id",
                  "patient_id",
                  "createdAt",
                  "updatedAt",
                  "doctor_id",
                ];

                const filteredData = Object.fromEntries(
                  Object.entries(editData).filter(
                    ([key]) => !excluded.includes(key)
                  )
                );

                await handleUpdate(filteredData);
              }}
              className="btn-primary"
            >
              Update Note
            </button>
          </div>
        </div>
      </Modal>

    </>
  );
};

export default ClinicalNotes;
